import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SaveButton from "../../../../../components/SaveButton";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import {
  addAutopay,
  savingAutopay,
  rowForAutopay,
  hasAutopay,
  editAutopayName,
} from "../atoms/atoms";
import useAddAutopay from "../hook/useAddAutopay";
import { IVendorConfigurationRepository } from "../type";
import AutopayDetails from "./autoPayDetails";

interface AutopayFormProps {
  isAdd: boolean;
  setIsAdd;
}

export const AutopayForm: React.FC<AutopayFormProps> = ({
  isAdd,
  setIsAdd,
}) => {
  const [isAddState, setAddState] = useRecoilState(addAutopay);
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(savingAutopay);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForAutopay);
  const queryClient = useQueryClient();
  const [hasAutopays, setHasAutopay] = useRecoilState(hasAutopay);
  const setEditState = useSetRecoilState(editAutopayName);
  const [newAutopay, setnewAutopay] =
    useState<IVendorConfigurationRepository>();
  const useFormMethods = useForm<IVendorConfigurationRepository>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;


  const {
    mutate: AutopayRows,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddAutopay();

  const onSubmit = (data: IVendorConfigurationRepository) => {
    setnewAutopay({
      ...data,
      isActive: data.isActive ? true : false,
    });

    if (isAdd) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };

  const handleSave = () => {
    setSaveModal(false);
    AutopayRows(newAutopay);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setHasAutopay(false);
    setIsSaving(false);
    setIsAdd(false);
  };

  // Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501 || addResult.data === "Duplicate") {
      toast.fire("Failed", "Autopay Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Autopay Added Successfully.", "success");
      queryClient.invalidateQueries("useGetAutopay");
      handleCancel();
      setAddState(false);
      setIsAdd(false);
      setHasAutopay(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Autopay added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Autopay! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("useGetAutopay");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Autopay! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("useGetAutopay");
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Autopay! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("useGetAutopay");
    setAddState(false);
  }

  return (
    <>
      <div className="pl-3 space-y-2 border-l-2 border-primary shadow-lg">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row w-full justify-between items-center text-quinary font-bold font-sans text-lg">
              <div className="font-bold pt-1 pb text-primary text-base">
                Add New Autopay
              </div>
              <div className="flex justify-between gap-2 mr-2 pb-2 pt-3">
                <SaveButton
                  className={`flex items-left shadow-md lg:text-center font-semibold text-white justify-left gap-x-1 rounded-full bg-senary hover:bg-senary px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
              ${!hasAutopays ? "opacity-50" : "opactiy-100"}`}
                  disabled={!hasAutopays}
                  onClick={() => setSaveModal(true)}
                  type="submit"
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 mr-3 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <AutopayDetails />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={"Add Autopay"}
          qConfirmation={"Are you sure you want to Add this data?"}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default AutopayForm;


